import { useRef, useState, useEffect, FC } from "react";
import { Box, VStack, Center, HStack, Button } from "@chakra-ui/react";
import dayjs from "dayjs";

const generateDays = () => {
  const days = [];
  const today = dayjs();
  for (let i = 0; i < 365; i++) {
    const currentDay = today.add(i, "day");
    days.push({
      title: i === 0 ? "Today" : currentDay.format("MMM D, YY"),
      value: currentDay,
    });
  }
  return days;
};

let days = generateDays();
let hours = Array.from({ length: 13 }, (_, i) => i.toString().padStart(2, "0"));
let minutes = Array.from({ length: 60 }, (_, i) =>
  i.toString().padStart(2, "0")
);
let meridians = ["AM", "PM"];

// Repeat hours, minutes and meridians for infinite scrolling
days = Array.from({ length: 2 }, (_, __) => days).flat();
hours = Array.from({ length: 10 }, (_, __) => hours).flat();
minutes = Array.from({ length: 10 }, (_, __) => minutes).flat();
meridians = Array.from({ length: 10 }, (_, __) => meridians).flat();

// const getInitialDayIndex = (selectedDate: Date | undefined) => {
//   const defaultInitialIndex = days.length / 2 - 3;
//   if (!selectedDate) return defaultInitialIndex;
//   const index = days
//     .slice(days.length / 2, days.length)
//     .findIndex((day) => day.value.isSame(selectedDate, "day"));
//   return index === -1 ? defaultInitialIndex : index + days.length / 2;
// };

const WheelDateTimePicker: FC<{
  onSelectDateTime: (date: Date) => void;
  selectedDate?: Date;
  includePreviousDays?: boolean;
}> = ({ onSelectDateTime, selectedDate, includePreviousDays }) => {
  // Initialize selected values with the middle of the array
  const [selectedDay, setSelectedDay] = useState(days.length / 2 - 3);
  const [selectedHour, setSelectedHour] = useState(hours.length / 10 - 3);
  const [selectedMinute, setSelectedMinute] = useState(minutes.length / 10 - 3);
  const [selectedMeridian, setSelectedMeridian] = useState(
    meridians.length / 10 - 2
  );

  // const [selectedDay, setSelectedDay] = useState(0);
  // const [selectedHour, setSelectedHour] = useState(0);
  // const [selectedMinute, setSelectedMinute] = useState(0);
  // const [selectedMeridian, setSelectedMeridian] = useState(0);

  const handleSelectDate = () => {
    // Get the selected day, hour, minute, and meridian
    const selectedDayStr = days[selectedDay].value;
    const selectedHourNum = hours[selectedHour];
    const selectedMinuteStr = minutes[selectedMinute];
    const selectedMeridianStr = meridians[selectedMeridian];

    // Adjust hour for AM/PM (12-hour format)
    let hour = +selectedHourNum;
    if (selectedMeridianStr === "PM" && hour !== 12) {
      hour += 12; // Convert PM hours to 24-hour format
    } else if (selectedMeridianStr === "AM" && hour === 12) {
      hour = 0; // Convert 12 AM to 00 (midnight)
    }

    // Use dayjs to create a valid datetime string
    const selectedDate = new Date(
      selectedDayStr.year(),
      selectedDayStr.month(),
      selectedDayStr.date(),
      hour,
      +selectedMinuteStr,
      0
    );

    console.log("Selected Date:", selectedDate); // This will log the selected date in Date object format

    onSelectDateTime(selectedDate);
  };

  return (
    <VStack spacing={0}>
      <VStack position="relative" spacing="0px" w="full" mx="55.5px">
        <HStack spacing="0px" justifyContent="center" my="55.5px">
          {/* Day Wheel */}
          <RenderedWheel
            items={days.map((day) => day.title)}
            selectedItem={selectedDay}
            onSetSelectedItem={setSelectedDay}
            w="177px"
          />

          <HStack spacing="27px">
            {/* Hour Wheel */}
            <RenderedWheel
              items={hours}
              selectedItem={selectedHour}
              onSetSelectedItem={setSelectedHour}
              w="27px"
            />

            {/* Minute Wheel */}
            <RenderedWheel
              items={minutes}
              selectedItem={selectedMinute}
              onSetSelectedItem={setSelectedMinute}
              w="27px"
            />

            {/* AM/PM Wheel */}
            <RenderedWheel
              items={meridians}
              selectedItem={selectedMeridian}
              onSetSelectedItem={setSelectedMeridian}
              w="39px"
              h="70px"
            />
          </HStack>
        </HStack>
        {/* Highlighted Row */}
        <Center
          position="absolute"
          top="44%" // Center row
          // top="55.5px" // Center row
          w="full"
          h="40px"
          borderTop="1px solid #CCCCCC"
          borderBottom="1px solid #CCCCCC"
          zIndex={1}
          pointerEvents="none"
        />
      </VStack>
      <Button
        w="full"
        h="56px"
        color="white"
        fontSize="18px"
        fontWeight={500}
        bgColor="primary.500"
        borderRadius="8px"
        onClick={handleSelectDate}
        lineHeight="22px"
      >
        Save
      </Button>
    </VStack>
  );
};

const RenderedWheel: FC<{
  items: (string | number)[];
  selectedItem: number;
  w?: string;
  h?: string;
  onSetSelectedItem: (index: number) => void;
}> = ({ items, selectedItem, w, h, onSetSelectedItem }) => {
  const isMeridianItem = items[0] === "AM" || items[0] === "PM";
  const isHourItem = +items[14] < 12;
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isMeridianOrHourFirstRender, setIsMeridianOrHourFirstRender] =
    useState(isMeridianItem || isHourItem);
  const itemHeight = 40; // Height of each item

  useEffect(() => {
    setTimeout(() => {
      if (scrollRef.current) {
        // Snap to the selected item on initial render
        scrollRef.current.scrollTo({
          top: selectedItem * itemHeight,
          behavior: "smooth",
        });
      }
    }, 1000);
  }, []);

  const handleScroll = () => {
    if (scrollRef.current) {
      // Get position of item in the middle of the wheel
      const scrollTop =
        scrollRef.current.scrollTop + (isMeridianItem ? 0 : itemHeight * 3);
      // const scrollTop = scrollRef.current.scrollTop + itemHeight * 3;
      const index = Math.round(scrollTop / itemHeight); // Find the closest item
      onSetSelectedItem(index);
    }

    if (isMeridianItem && isMeridianOrHourFirstRender) {
      setIsMeridianOrHourFirstRender(false);
    }
  };

  return (
    <Box
      ref={scrollRef}
      onScroll={() => {
        // Debounce selection to avoid mid-scroll changes
        setTimeout(handleScroll, 500);
      }}
      h={h ?? "260px"}
      w={w}
      mt={isMeridianOrHourFirstRender && isMeridianItem ? "24px" : 0}
      mb={isMeridianOrHourFirstRender && isHourItem ? "-2px" : 0}
      overflowY="scroll"
      scrollSnapType="y mandatory"
      sx={{
        "&::-webkit-scrollbar": {
          width: 0,
          background: "transparent",
        },
        "&::-webkit-scrollbar-thumb": {
          display: "none",
        },
      }}
    >
      {items.map((item, index) => {
        const itemIsSelected = selectedItem === index;
        return (
          <Box
            key={index}
            h={`${itemHeight}px`}
            w="full"
            display="flex"
            justifyContent="center"
            alignItems="center"
            lineHeight={itemIsSelected ? "28px" : "18px"}
            fontSize={itemIsSelected ? "24px" : "18px"}
            fontWeight={itemIsSelected ? "500" : "normal"}
            color={itemIsSelected ? "dark.200" : "grey.150"}
            scrollSnapAlign="center"
          >
            {item}
          </Box>
        );
      })}
    </Box>
  );
};

export default WheelDateTimePicker;
